import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Step,
  Stepper,
  StepLabel,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { apiService, Client, Bundle, BundleField, BundleAd, Ad } from '../services/api';

interface ClientCreateAdModalProps {
  open: boolean;
  onClose: () => void;
  clientSecret: string;
  onAdCreated: () => void;
  editAd?: Ad | null;
}

const ClientCreateAdModal: React.FC<ClientCreateAdModalProps> = ({
  open,
  onClose,
  clientSecret,
  onAdCreated,
  editAd
}) => {
  const [client, setClient] = useState<Client | null>(null);
  const [loadingClient, setLoadingClient] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [selectedBundleAd, setSelectedBundleAd] = useState<BundleAd | null>(null);
  const [matchId, setMatchId] = useState('');
  const [bundleFieldValues, setBundleFieldValues] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const steps = ['Select Bundle', 'Configure Ad'];

  // Fetch client data when modal opens
  useEffect(() => {
    if (open && clientSecret) {
      fetchClientData();
    }
  }, [open, clientSecret]);

  // Pre-populate form when editing
  useEffect(() => {
    if (editAd && client && open) {
      // Use the bundle data directly from bundle_ad if available, otherwise find from client bundles
      if (editAd.bundle_ad) {
        const bundle = editAd.bundle_ad.bundle || client.bundles?.find(b => b.id === editAd.bundle_ad?.fk_bundles);
        if (bundle) {
          setSelectedBundle(bundle);
          setSelectedBundleAd(editAd.bundle_ad);
          setActiveStep(1);

          // Set match ID
          if (editAd.tx_match_id) {
            setMatchId(editAd.tx_match_id);
          }

          // Set bundle field values from client_field_values
          if (editAd.client_field_values) {
            setBundleFieldValues(editAd.client_field_values);
          }
        }
      }
    } else if (!editAd && open) {
      // Reset form when not editing
      setActiveStep(0);
      setSelectedBundle(null);
      setSelectedBundleAd(null);
      setMatchId('');
      setBundleFieldValues({});
    }
  }, [editAd, client, open]);

  const fetchClientData = async () => {
    setLoadingClient(true);
    setError('');
    try {
      // Fetch real client data using the client secret
      const clientData = await apiService.getClientBySecret(clientSecret);
      setClient(clientData);
      
      // Reset form state only if not editing
      if (!editAd) {
        setActiveStep(0);
        setSelectedBundle(null);
        setSelectedBundleAd(null);
        setMatchId('');
        setBundleFieldValues({});
      }
    } catch (error: any) {
      let errorMessage = 'Failed to load client data';
      
      if (error?.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map((err: any) => 
            typeof err === 'string' ? err : err.msg || 'Validation error'
          ).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      setError(errorMessage);
      console.error('Error fetching client data:', error);
    } finally {
      setLoadingClient(false);
    }
  };

  const handleBundleSelect = (event: SelectChangeEvent<string>) => {
    const bundleId = parseInt(event.target.value);
    const bundle = client?.bundles?.find(b => b.id === bundleId);
    if (bundle) {
      setSelectedBundle(bundle);
      setSelectedBundleAd(null);
      setBundleFieldValues({});
      // Move to next step
      setActiveStep(1);
    }
  };

  const handleBundleAdSelect = (event: SelectChangeEvent<string>) => {
    const adId = parseInt(event.target.value);
    const bundleAd = selectedBundle?.bundle_ads.find(ad => ad.id === adId);
    if (bundleAd) {
      setSelectedBundleAd(bundleAd);
    }
  };

  const handleFieldValueChange = (fieldName: string, value: string) => {
    setBundleFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBundleAd || !matchId.trim()) return;

    setIsLoading(true);
    setError('');

    try {
      if (editAd) {
        // Update existing ad using PATCH
        await apiService.updateClientAd(
          clientSecret,
          editAd.id,
          {
            name: selectedBundleAd.tx_name,
            match_id: matchId.trim(),
            bundle_fields: bundleFieldValues,
            bundle_ad_id: selectedBundleAd.id
          }
        );
      } else {
        // Create new ad using POST
        await apiService.createClientAd(
          clientSecret,
          {
            name: selectedBundleAd.tx_name,
            match_id: matchId.trim(),
            bundle_fields: bundleFieldValues,
            bundle_ad_id: selectedBundleAd.id
          }
        );
      }
      
      onAdCreated();
      handleClose();
    } catch (error: any) {
      let errorMessage = editAd ? 'Failed to update ad' : 'Failed to create ad';
      
      if (error?.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Handle validation errors array
          errorMessage = detail.map((err: any) => 
            typeof err === 'string' ? err : err.msg || 'Validation error'
          ).join(', ');
        } else if (typeof detail === 'object') {
          // Handle validation error object
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setActiveStep(0);
      setSelectedBundle(null);
      setSelectedBundleAd(null);
      setMatchId('');
      setBundleFieldValues({});
      setError('');
      setClient(null);
      onClose();
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    setSelectedBundleAd(null);
    setBundleFieldValues({});
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {editAd ? 'Edit Ad' : 'Create New Ad'}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {loadingClient ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {activeStep === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Step 1: Select Bundle
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose which bundle you want to create an ad for.
                  </Typography>

                  <FormControl fullWidth>
                    <InputLabel id="bundle-select-label">Select Bundle</InputLabel>
                    <Select
                      labelId="bundle-select-label"
                      value={selectedBundle?.id?.toString() || ''}
                      onChange={handleBundleSelect}
                      label="Select Bundle"
                      disabled={isLoading}
                    >
                      {client?.bundles?.map((bundle) => (
                        <MenuItem key={bundle.id} value={bundle.id.toString()}>
                          {bundle.tx_client_bundle_name ? `${bundle.tx_client_bundle_name} (v${bundle.tx_version})` : `${bundle.tx_name} (v${bundle.tx_version})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {(!client?.bundles || client.bundles.length === 0) && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      No bundles available. Please contact your administrator to associate bundles with your account.
                    </Alert>
                  )}
                </Box>
              )}

              {activeStep === 1 && selectedBundle && (
                <Box component="form" onSubmit={handleSubmit}>
                  <Typography variant="h6" gutterBottom>
                    Step 2: Configure Ad
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: 'primary.light', 
                    borderRadius: 1, 
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'primary.contrastText' }}>
                      Selected Bundle: {selectedBundle.tx_client_bundle_name ? `${selectedBundle.tx_client_bundle_name} (v${selectedBundle.tx_version})` : `${selectedBundle.tx_name} (v${selectedBundle.tx_version})`}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={handleBack}
                      sx={{ color: 'primary.contrastText', borderColor: 'primary.contrastText' }}
                    >
                      Change Bundle
                    </Button>
                  </Box>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="bundle-ad-select-label">Select Ad Type</InputLabel>
                    <Select
                      labelId="bundle-ad-select-label"
                      value={selectedBundleAd?.id?.toString() || ''}
                      onChange={handleBundleAdSelect}
                      label="Select Ad Type"
                      disabled={isLoading}
                    >
                      {selectedBundle.bundle_ads.map((bundleAd) => (
                        <MenuItem key={bundleAd.id} value={bundleAd.id.toString()}>
                          {bundleAd.tx_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Match ID"
                    value={matchId}
                    onChange={(e) => setMatchId(e.target.value)}
                    required
                    disabled={isLoading}
                    sx={{ mb: 2 }}
                    helperText="Unique identifier for this ad"
                  />

                  {selectedBundle.bundle_fields.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Bundle Fields
                      </Typography>
                      {selectedBundle.bundle_fields.map((field) => (
                        <FormControl key={field.id} fullWidth sx={{ mb: 2 }}>
                          <InputLabel id={`field-${field.id}-label`}>
                            {field.tx_client_field_name || field.tx_name}
                          </InputLabel>
                          <Select
                            labelId={`field-${field.id}-label`}
                            value={bundleFieldValues[field.tx_name] || ''}
                            onChange={(e) => handleFieldValueChange(field.tx_name, e.target.value)}
                            label={field.tx_client_field_name || field.tx_name}
                            disabled={isLoading}
                          >
                            <MenuItem value="">
                              <em>Choose an option...</em>
                            </MenuItem>
                            {field.ar_options?.map((option, index) => (
                              <MenuItem key={index} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                          {field.tx_description && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                              {field.tx_description}
                            </Typography>
                          )}
                        </FormControl>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {activeStep === 1 && !loadingClient && (
          <Button onClick={handleBack} disabled={isLoading}>
            Back
          </Button>
        )}
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        {activeStep === 1 && !loadingClient && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading || !selectedBundleAd || !matchId.trim()}
          >
            {isLoading ? (editAd ? 'Updating...' : 'Creating...') : (editAd ? 'Update Ad' : 'Create Ad')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClientCreateAdModal;